import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  TrendingUp, Users, DollarSign, CloudSun, Sun, Cloud, CloudRain, CloudDrizzle,
  CloudLightning, Snowflake, Eye, EyeOff, Save, Info,
} from "lucide-react";
import { toast } from "sonner";

/**
 * EventPredictionsCard
 * --------------------
 * Organizer-only planning card. Three predictions:
 *   • Weather (Open-Meteo, updates daily inside a 24h server cache)
 *   • Attendance (heuristic → organizer override → past-actual)
 *   • Revenue (attendance × avg-basket by category)
 *
 * Each row has its own "Share with vendors" switch so the organizer can
 * keep private numbers to themselves and only surface the ones they're
 * comfortable with (e.g. share weather, hide revenue guess).
 *
 * Post-event, a "How big was it really?" input records actual attendance
 * so future runs of the same event lean on the real number.
 */
const ICONS = {
  sun: Sun, "cloud-sun": CloudSun, cloud: Cloud,
  "cloud-rain": CloudRain, "cloud-drizzle": CloudDrizzle,
  "cloud-lightning": CloudLightning, snowflake: Snowflake,
};

const money = (n) => `$${Math.round(n).toLocaleString()}`;
const num = (n) => Number(n || 0).toLocaleString();

export default function EventPredictionsCard({ eventId, eventDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minInput, setMinInput] = useState("");
  const [maxInput, setMaxInput] = useState("");
  const [actualInput, setActualInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/predictions`);
      setData(data);
      setMinInput(data.estimated_attendance_min ?? "");
      setMaxInput(data.estimated_attendance_max ?? "");
      setActualInput(data.actual_attendance ?? "");
    } catch {
      toast.error("Couldn't load predictions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [eventId]);

  const setVisibility = async (key, value) => {
    const next = { ...(data?.visibility || {}), [key]: value };
    // Optimistic UI — flip immediately, revert on failure.
    setData((prev) => ({ ...prev, visibility: next }));
    try {
      const { data: fresh } = await api.put(`/events/${eventId}/predictions`, {
        predictions_visibility: next,
      });
      setData(fresh);
    } catch (err) {
      toast.error("Couldn't update — try again");
      setData((prev) => ({ ...prev, visibility: data.visibility }));
    }
  };

  const saveOverrides = async () => {
    const payload = {};
    payload.estimated_attendance_min = minInput === "" ? 0 : parseInt(minInput, 10);
    payload.estimated_attendance_max = maxInput === "" ? 0 : parseInt(maxInput, 10);
    if (payload.estimated_attendance_min > payload.estimated_attendance_max
        && payload.estimated_attendance_max !== 0) {
      toast.error("Attendance min can't be higher than max.");
      return;
    }
    setSaving(true);
    try {
      const { data: fresh } = await api.put(`/events/${eventId}/predictions`, payload);
      setData(fresh);
      toast.success("Saved your estimate.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };

  const saveActual = async () => {
    const val = parseInt(actualInput, 10);
    if (Number.isNaN(val) || val < 0) {
      toast.error("Enter a whole number");
      return;
    }
    setSaving(true);
    try {
      const { data: fresh } = await api.post(`/events/${eventId}/predictions/actual`, {
        actual_attendance: val,
      });
      setData(fresh);
      toast.success("Locked in — future events will lean on this number.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="card-warm rounded-sm p-6" data-testid="predictions-card">
      <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading predictions…</div>
    </div>
  );
  if (!data) return null;

  const isPast = eventDate ? new Date(eventDate + "T23:59:59") < new Date() : false;
  const weather = data.weather;
  const WeatherIcon = ICONS[weather?.icon] || CloudSun;

  return (
    <div className="card-warm rounded-sm p-6" data-testid="predictions-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--moss)" }}>
            <TrendingUp className="h-3 w-3" /> Estimates
          </div>
          <div className="font-serif text-2xl mt-1">Predictions for this event</div>
          <p className="mt-1 text-sm max-w-lg" style={{ color: "var(--ink-soft)" }}>
            Rough estimates to help you plan. Toggle each row on to share it with
            vendors on the public event page — off to keep it private for your planning.
          </p>
        </div>
      </div>

      {/* --- Weather -------------------------------------------------- */}
      <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--sand)" }}
           data-testid="predictions-weather">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <WeatherIcon className="h-8 w-8 mt-1 shrink-0" style={{ color: "var(--moss)" }} />
            <div>
              <div className="overline" style={{ color: "var(--ink-soft)" }}>Weather forecast</div>
              {weather?.unavailable ? (
                <div className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}
                     data-testid="predictions-weather-unavailable">
                  {weather.reason}
                </div>
              ) : (
                <>
                  <div className="font-serif text-2xl mt-0.5" data-testid="predictions-weather-summary">
                    {weather.label} · {weather.high_f}° / {weather.low_f}°F
                  </div>
                  <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                    {weather.precip_pct}% chance of rain · {weather.city_matched} · from {weather.source}
                  </div>
                </>
              )}
            </div>
          </div>
          <ShareToggle
            testId="predictions-share-weather"
            label="Share"
            checked={data.visibility.weather}
            onChange={(v) => setVisibility("weather", v)}
          />
        </div>
      </div>

      {/* --- Attendance ---------------------------------------------- */}
      <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--sand)" }}
           data-testid="predictions-attendance">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <Users className="h-8 w-8 mt-1 shrink-0" style={{ color: "var(--moss)" }} />
            <div>
              <div className="overline" style={{ color: "var(--ink-soft)" }}>Expected attendance</div>
              <div className="font-serif text-2xl mt-0.5" data-testid="predictions-attendance-range">
                {num(data.attendance.min)} – {num(data.attendance.max)}
              </div>
              <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                {sourceLabel(data.attendance.source)}
              </div>
            </div>
          </div>
          <ShareToggle
            testId="predictions-share-attendance"
            label="Share"
            checked={data.visibility.attendance}
            onChange={(v) => setVisibility("attendance", v)}
          />
        </div>
        {data.attendance.caveats?.map((c, i) => (
          <p key={i} className="mt-2 text-xs flex items-start gap-1.5"
             style={{ color: "var(--ink-soft)" }}>
            <Info className="h-3 w-3 mt-0.5 shrink-0" /> {c}
          </p>
        ))}
        <div className="mt-3 grid sm:grid-cols-3 gap-2" data-testid="predictions-attendance-override">
          <div>
            <Label className="text-xs uppercase tracking-widest">Your low estimate</Label>
            <Input
              data-testid="predictions-attendance-min"
              type="number" min="0" value={minInput}
              onChange={(e) => setMinInput(e.target.value)}
              placeholder={String(data.attendance.min)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Your high estimate</Label>
            <Input
              data-testid="predictions-attendance-max"
              type="number" min="0" value={maxInput}
              onChange={(e) => setMaxInput(e.target.value)}
              placeholder={String(data.attendance.max)}
            />
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              data-testid="predictions-attendance-save"
              onClick={saveOverrides}
              disabled={saving}
              className="btn-outline rounded-full w-full"
            >
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Use my numbers"}
            </Button>
          </div>
        </div>
      </div>

      {/* --- Revenue -------------------------------------------------- */}
      <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--sand)" }}
           data-testid="predictions-revenue">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <DollarSign className="h-8 w-8 mt-1 shrink-0" style={{ color: "var(--moss)" }} />
            <div>
              <div className="overline" style={{ color: "var(--ink-soft)" }}>Estimated total customer spend</div>
              <div className="font-serif text-2xl mt-0.5" data-testid="predictions-revenue-total">
                {money(data.revenue.total_min)} – {money(data.revenue.total_max)}
              </div>
              <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                ≈ {money(data.revenue.per_vendor_min)} – {money(data.revenue.per_vendor_max)} per vendor
              </div>
            </div>
          </div>
          <ShareToggle
            testId="predictions-share-revenue"
            label="Share"
            checked={data.visibility.revenue}
            onChange={(v) => setVisibility("revenue", v)}
          />
        </div>
        {data.revenue.caveats?.map((c, i) => (
          <p key={i} className="mt-2 text-xs flex items-start gap-1.5"
             style={{ color: "var(--ink-soft)" }}>
            <Info className="h-3 w-3 mt-0.5 shrink-0" /> {c}
          </p>
        ))}
      </div>

      {/* --- Post-event actual --------------------------------------- */}
      {isPast && (
        <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--sand)" }}
             data-testid="predictions-actual-form">
          <div className="overline" style={{ color: "var(--terracotta)" }}>How big was it really?</div>
          <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
            Record what actually showed up — future runs of this event will lean
            on this number instead of the heuristic.
          </p>
          <div className="mt-3 flex gap-2 flex-wrap max-w-md">
            <Input
              data-testid="predictions-actual-input"
              type="number" min="0"
              value={actualInput}
              onChange={(e) => setActualInput(e.target.value)}
              placeholder="e.g. 950"
              className="flex-1"
            />
            <Button
              type="button"
              data-testid="predictions-actual-save"
              onClick={saveActual}
              disabled={saving || actualInput === ""}
              className="btn-primary rounded-full"
            >
              <Save className="h-4 w-4 mr-1.5" /> Lock it in
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareToggle({ testId, label, checked, onChange }) {
  return (
    <label
      className="inline-flex items-center gap-2 shrink-0 cursor-pointer"
      data-testid={testId}
    >
      {checked ? <Eye className="h-4 w-4" style={{ color: "var(--moss)" }} />
               : <EyeOff className="h-4 w-4" style={{ color: "var(--ink-soft)" }} />}
      <span className="text-xs uppercase tracking-widest"
            style={{ color: checked ? "var(--moss)" : "var(--ink-soft)" }}>
        {label}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function sourceLabel(source) {
  if (source === "past_actual") return "Based on how last time went (±15%)";
  if (source === "organizer") return "Your own estimate";
  return "Heuristic estimate — refine below if you have better numbers";
}
